import React, { useState, useCallback } from "react";
import { MAX_REPORT_IMAGES } from "../../constants";
import "../styles/ImageEditor.css";

interface ImageEditorProps {
  images: string[]; // Array of image URLs (existing) or blob URLs (new)
  onImagesChange: (images: string[], newFiles: File[]) => void;
  disabled?: boolean;
  minImages?: number;
  maxImages?: number;
}

const ImageEditor: React.FC<ImageEditorProps> = ({
  images,
  onImagesChange,
  disabled = false,
  minImages = 1,
  maxImages = MAX_REPORT_IMAGES,
}) => {
  const [newFiles, setNewFiles] = useState<File[]>([]);

  const handleAddImages = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled || !event.target.files) return;

      const files = Array.from(event.target.files);
      let validFiles = files.filter((file) => file.type.startsWith("image/"));

      if (validFiles.length === 0) {
        alert("Please select valid image files");
        event.target.value = "";
        return;
      }

      const remaining = maxImages - images.length;
      if (validFiles.length > remaining) {
        alert(`You can attach up to ${maxImages} images.`);
        validFiles = validFiles.slice(0, Math.max(0, remaining));
      }

      if (validFiles.length === 0) {
        event.target.value = "";
        return;
      }

      // Create blob URLs for preview
      const blobUrls = validFiles.map((file) => URL.createObjectURL(file));
      const updatedImages = [...images, ...blobUrls];
      const updatedFiles = [...newFiles, ...validFiles];

      setNewFiles(updatedFiles);
      onImagesChange(updatedImages, updatedFiles);

      // Reset input
      event.target.value = "";
    },
    [images, newFiles, onImagesChange, disabled, maxImages]
  );

  const handleDeleteImage = useCallback(
    (index: number) => {
      if (disabled) return;

      if (images.length <= minImages) {
        alert(`You must keep at least ${minImages} image(s)`);
        return;
      }

      if (!window.confirm("Are you sure you want to delete this image?"))
        return;

      const imageToDelete = images[index];
      let updatedFiles = newFiles;

      // Revoke blob URL if it's a new image
      if (imageToDelete.startsWith("blob:")) {
        URL.revokeObjectURL(imageToDelete);
        // Remove from newFiles array
        const fileIndex = images
          .slice(0, index)
          .filter((img) => img.startsWith("blob:")).length;
        updatedFiles = newFiles.filter((_, i) => i !== fileIndex);
        setNewFiles(updatedFiles);
      }

      const updatedImages = images.filter((_, i) => i !== index);
      onImagesChange(updatedImages, updatedFiles);
    },
    [images, newFiles, onImagesChange, disabled, minImages]
  );

  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (disabled || fromIndex === toIndex) return;

      const updatedImages = [...images];
      const [movedImage] = updatedImages.splice(fromIndex, 1);
      updatedImages.splice(toIndex, 0, movedImage);

      onImagesChange(updatedImages, newFiles);
    },
    [images, newFiles, onImagesChange, disabled]
  );

  return (
    <div className="image-editor">
      <div className="images-grid">
        {images.map((image, index) => (
          <div key={index} className="image-item">
            <img src={image} alt={"Pollution"} className="image-preview" />
            <div className="image-overlay">
              <div className="image-actions">
                {index > 0 && (
                  <button
                    className="image-action-btn"
                    onClick={() => handleReorder(index, index - 1)}
                    disabled={disabled}
                    title="Move left"
                  >
                    ←
                  </button>
                )}
                <button
                  className="image-action-btn delete-btn"
                  onClick={() => handleDeleteImage(index)}
                  disabled={disabled}
                  title="Delete"
                >
                  ×
                </button>
                {index < images.length - 1 && (
                  <button
                    className="image-action-btn"
                    onClick={() => handleReorder(index, index + 1)}
                    disabled={disabled}
                    title="Move right"
                  >
                    →
                  </button>
                )}
              </div>
            </div>
            {index === 0 && <div className="main-badge">Main</div>}
          </div>
        ))}

        {/* Add Image Button */}
        {!disabled && images.length < maxImages && (
          <label className="add-image-btn">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleAddImages}
              disabled={disabled}
              style={{ display: "none" }}
            />
            <div className="add-image-content">
              <span className="add-icon">+</span>
              <span className="add-text">Add Image</span>
            </div>
          </label>
        )}
      </div>
    </div>
  );
};

export default ImageEditor;
